/**
 * Resolve photo albums from CMS expertGallery (supports legacy flat photos[]).
 */
import { getMediaUrl } from './mediaUrl';

function photoHasMedia(photo) {
  return Boolean(getMediaUrl(photo));
}

function legacyPhotosWithMedia(expertGallery) {
  return (expertGallery.photos || []).filter(photoHasMedia);
}

function distributePhotosAcrossAlbums(albums, photos) {
  if (!albums.length) {
    return [{ id: 'general', title: 'Galerie', photos }];
  }
  if (albums.length === 1) {
    return [{ ...albums[0], photos }];
  }

  const chunkSize = Math.ceil(photos.length / albums.length);
  return albums.map((album, index) => ({
    ...album,
    photos: photos.slice(index * chunkSize, (index + 1) * chunkSize),
  }));
}

export function resolvePhotoAlbums(expertGallery) {
  if (!expertGallery || typeof expertGallery !== 'object') return [];

  const legacyPhotos = legacyPhotosWithMedia(expertGallery);

  if (Array.isArray(expertGallery.albums) && expertGallery.albums.length > 0) {
    const albums = expertGallery.albums
      .filter((a) => a && (a.photos?.length || a.title))
      .map((album) => ({
        ...album,
        photos: (album.photos || []).filter(photoHasMedia),
      }));

    const albumMediaCount = albums.reduce((sum, album) => sum + album.photos.length, 0);

    // Album shells saved without image URLs — use legacy photos still in KV.
    if (albumMediaCount === 0 && legacyPhotos.length > 0) {
      return distributePhotosAcrossAlbums(albums, legacyPhotos);
    }

    return albums.filter((album) => album.photos.length > 0 || album.title);
  }

  if (legacyPhotos.length === 0) return [];

  return [{ id: 'general', title: 'Galerie', photos: legacyPhotos }];
}

/**
 * Resolve video albums from CMS expertGallery (supports legacy flat videos[]).
 */
export function resolveVideoAlbums(expertGallery) {
  if (!expertGallery || typeof expertGallery !== 'object') return [];

  const legacyVideos = expertGallery.videos || [];

  if (Array.isArray(expertGallery.videoAlbums) && expertGallery.videoAlbums.length > 0) {
    const albums = expertGallery.videoAlbums.filter((a) => a && (a.videos?.length || a.title));
    const hasMedia = albums.some((a) => (a.videos || []).length > 0);
    if (!hasMedia && legacyVideos.length > 0) {
      return [{ id: 'general', title: 'Vidéos', videos: legacyVideos }];
    }
    return albums;
  }

  if (legacyVideos.length === 0) return [];

  return [{ id: 'general', title: 'Vidéos', videos: legacyVideos }];
}

export function flattenAlbumPhotos(albums) {
  if (!Array.isArray(albums)) return [];
  return albums.flatMap((album) => album.photos || []);
}

export function flattenAlbumVideos(albums) {
  if (!Array.isArray(albums)) return [];
  return albums.flatMap((album) => album.videos || []);
}

export function countGalleryVideos(expertGallery) {
  return resolveVideoAlbums(expertGallery).reduce(
    (sum, album) => sum + (album.videos?.length || 0),
    0
  );
}
